import {
  Autocomplete,
  TextField,
  TextFieldProps,
  AutocompleteProps,
  ChipTypeMap,
  CircularProgress,
} from '@mui/material'
import { ElementType } from 'react'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'

export type AutocompleteInputProps<
  TValue,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  AutocompleteProps<
    TValue,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >,
  'name' | 'options' | 'renderInput'
> & {
  name: TName
  control: Control<TFieldValues>
  options: TValue[]
  label?: TextFieldProps['label']
  textFieldProps?: Omit<TextFieldProps, 'name' | 'required' | 'label'>
}

export const AutocompleteInput = <
  TValue,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  textFieldProps,
  label,
  loading,
  ...props
}: AutocompleteInputProps<
  TValue,
  Multiple,
  DisableClearable,
  FreeSolo,
  ChipComponent,
  TFieldValues,
  TName
>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          noOptionsText="Nenhum resultado encontrado"
          onChange={(_, newValue) => field.onChange(newValue ?? '')}
          {...props}
          renderInput={(params) => (
            <TextField
              {...params}
              {...textFieldProps}
              label={label}
              helperText={error?.message}
              error={Boolean(error)}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && (
                        <CircularProgress color="inherit" size={20} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  ...textFieldProps?.slotProps?.input,
                },
              }}
            />
          )}
        />
      )}
    />
  )
}
